// src/email/components/EmailBodyFrame.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Isolated iframe component for rendering HTML/Plain-text emails.
 * Prevents host CSS (Bootstrap, AntD, Tailwind, etc.) from corrupting email layouts,
 * prevents email CSS from leaking out to the CRM, safely opens external links in a new tab,
 * and dynamically resizes its height to fit the email without internal scrollbars.
 */
const QUOTED_ATTR = 'data-crm-quoted';
const SHOW_QUOTED_ATTR = 'data-crm-show-quoted';

// Markers the common mail clients put around the previous mail chain in a reply
const QUOTE_SELECTORS = [
  '.gmail_quote_container',
  '.gmail_quote',
  '.gmail-quote-header', // replies composed from this CRM
  '.gmail_extra',
  '#divRplyFwdMsg', // Outlook web
  '#appendonsend',
  '.yahoo_quoted',
  '.moz-cite-prefix', // Thunderbird
  '.protonmail_quote',
  '.zmail_extra',
  'blockquote[type="cite"]',
  'div[style*="border-top:solid #E1E1E1"]', // Outlook desktop
  'div[style*="border-top: solid #E1E1E1"]',
  'blockquote',
].join(',');

/**
 * Marks the quoted mail chain inside a reply so it can be collapsed (Gmail-style "•••").
 * Returns the marked HTML, or null when there is nothing to collapse.
 */
function markQuotedChain(rawHtml) {
  try {
    const doc = new DOMParser().parseFromString(rawHtml, 'text/html');
    const first = doc.body?.querySelector(QUOTE_SELECTORS);
    if (!first) return null;

    // Forwarded content is the actual email, never collapse it
    const lead = `${first.previousElementSibling?.textContent || ''} ${first.textContent || ''}`
      .trim()
      .slice(0, 300);
    if (/forwarded message|^-+\s*original message/i.test(lead)) return null;

    // The chain runs from the first marker to the end of the message
    const quoted = [];
    const prev = first.previousElementSibling;
    if (prev && prev.tagName === 'HR') quoted.push(prev);
    for (let el = first; el; el = el.nextElementSibling) quoted.push(el);
    quoted.forEach((el) => el.setAttribute(QUOTED_ATTR, ''));

    // Keep everything visible if the message has no content of its own
    const clone = doc.body.cloneNode(true);
    clone.querySelectorAll(`[${QUOTED_ATTR}]`).forEach((el) => el.remove());
    if (!clone.textContent.trim() && !clone.querySelector('img')) return null;

    if (/<html[\s>]/i.test(rawHtml)) {
      const doctype = /^\s*<!doctype/i.test(rawHtml) ? '<!DOCTYPE html>' : '';
      return `${doctype}${doc.documentElement.outerHTML}`;
    }
    return `${doc.head.innerHTML}${doc.body.innerHTML}`;
  } catch (err) {
    return null;
  }
}

export function EmailBodyFrame({ html, text }) {
  const iframeRef = useRef(null);
  const measureRef = useRef(null);
  const [frameHeight, setFrameHeight] = useState(250);
  const [showQuoted, setShowQuoted] = useState(false);

  const markedHtml = useMemo(
    () => (html && html.trim() ? markQuotedChain(html.trim()) : null),
    [html]
  );
  const hasQuoted = markedHtml !== null;

  useEffect(() => {
    setShowQuoted(false);
  }, [html, text]);

  // Prepare isolated HTML content with base target and safe email baseline
  const srcDocContent = useMemo(() => {
    if (html && html.trim()) {
      let content = markedHtml ?? html.trim();
      const baseTag = '<base target="_blank" rel="noopener noreferrer" />';
      
      const resetCss = `
        <style>
          html {
            box-sizing: border-box;
            -webkit-text-size-adjust: 100%;
          }
          body {
            margin: 0;
            padding: 8px 12px;
            background-color: #ffffff;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
            font-size: 14px;
            line-height: 1.5;
            color: #1e293b;
            word-wrap: break-word;
          }
          /* Allow email tables to size and center naturally */
          table {
            border-collapse: collapse;
          }
          /* Ensure images don't overflow the viewport */
          img {
            max-width: 100%;
            height: auto;
          }
          a {
            color: #2563eb;
          }
          /* Quoted mail chain stays collapsed until expanded from the "•••" toggle */
          html:not([${SHOW_QUOTED_ATTR}]) [${QUOTED_ATTR}] {
            display: none !important;
          }
        </style>
      `;

      if (content.includes('<head>')) {
        return content.replace('<head>', `<head>${baseTag}${resetCss}`);
      } else if (content.includes('<head ')) {
        return content.replace(/<head\b[^>]*>/i, (match) => `${match}${baseTag}${resetCss}`);
      } else if (content.includes('<html>')) {
        return content.replace('<html>', `<html><head>${baseTag}${resetCss}</head>`);
      } else if (content.includes('<html ')) {
        return content.replace(/<html\b[^>]*>/i, (match) => `${match}<head>${baseTag}${resetCss}</head>`);
      } else {
        return `<!DOCTYPE html><html><head><meta charset="utf-8" /><meta name="viewport" content="width=device-width, initial-scale=1.0" />${baseTag}${resetCss}</head><body>${content}</body></html>`;
      }
    }

    // Plain text fallback
    const escaped = (text || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\n/g, '<br/>');

    return `<!DOCTYPE html><html><head><meta charset="utf-8" /><base target="_blank" rel="noopener noreferrer" /><style>body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; font-size: 14px; line-height: 1.6; color: #1e293b; padding: 12px 16px; margin: 0; white-space: pre-wrap; word-break: break-word; background: #ffffff; }</style></head><body>${escaped}</body></html>`;
  }, [html, text, markedHtml]);

  // Expand / collapse the quoted chain inside the already-loaded frame
  const toggleQuoted = () => {
    const next = !showQuoted;
    setShowQuoted(next);
    try {
      const iframe = iframeRef.current;
      const doc = iframe?.contentDocument;
      if (!doc?.documentElement) return;
      doc.documentElement.toggleAttribute(SHOW_QUOTED_ATTR, next);
      measureRef.current?.();
    } catch (err) {}
  };

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    let resizeObserver = null;

    const measureAndResize = () => {
      try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!doc) return;

        const body = doc.body;
        const htmlEl = doc.documentElement;

        if (body && htmlEl) {
          // Measure the content itself rather than the document: the document is never
          // shorter than the frame, which would leave a gap under short / collapsed mails
          let height = 0;
          try {
            const range = doc.createRange();
            range.selectNodeContents(body);
            const bottom = range.getBoundingClientRect().bottom;
            if (bottom > 0) {
              const bodyStyle = doc.defaultView.getComputedStyle(body);
              height = Math.ceil(
                bottom +
                  (doc.defaultView.scrollY || 0) +
                  (parseFloat(bodyStyle.paddingBottom) || 0) +
                  (parseFloat(bodyStyle.marginBottom) || 0)
              );
              // In standards mode the body box also tracks its content (covers floats etc.)
              if (doc.compatMode === 'CSS1Compat') {
                height = Math.max(height, body.offsetTop + body.offsetHeight);
              }
            }
          } catch (e) {}

          if (!height) {
            height = Math.max(
              body.scrollHeight,
              body.offsetHeight,
              htmlEl.clientHeight,
              htmlEl.scrollHeight,
              htmlEl.offsetHeight
            );
          }

          if (height > 0) {
            // Add slight buffer so subpixels never clip the last line
            setFrameHeight(height + 4);
          }
        }
      } catch (err) {
        // Cross-origin fallback (should not occur with srcDoc)
      }
    };

    measureRef.current = measureAndResize;

    const handleLoad = () => {
      measureAndResize();

      try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (doc && doc.body) {
          // Monitor DOM changes inside the email
          if (window.ResizeObserver) {
            resizeObserver = new ResizeObserver(() => {
              measureAndResize();
            });
            resizeObserver.observe(doc.body);
          }

          // Monitor late-loading images
          const images = doc.querySelectorAll('img');
          images.forEach((img) => {
            if (!img.complete) {
              img.addEventListener('load', measureAndResize);
              img.addEventListener('error', measureAndResize);
            }
          });
        }
      } catch (e) {}
    };

    iframe.addEventListener('load', handleLoad);

    // Periodic measuring intervals for animated/lazy elements
    const t1 = setTimeout(measureAndResize, 100);
    const t2 = setTimeout(measureAndResize, 350);
    const t3 = setTimeout(measureAndResize, 800);
    const t4 = setTimeout(measureAndResize, 1800);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
      iframe.removeEventListener('load', handleLoad);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [srcDocContent]);

  return (
    <div className="email-body-frame-container">
      <iframe
        ref={iframeRef}
        srcDoc={srcDocContent}
        title="Email message body"
        className="email-isolated-frame"
        tabIndex={-1}
        style={{
          width: '100%',
          height: `${frameHeight}px`,
          border: 'none',
          display: 'block',
          background: '#ffffff',
          overflow: 'hidden',
        }}
        sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin"
      />
      {hasQuoted && (
        <button
          type="button"
          className="quoted-chain-toggle"
          title={showQuoted ? 'Hide trimmed content' : 'Show trimmed content'}
          aria-expanded={showQuoted}
          onClick={toggleQuoted}
        >
          •••
        </button>
      )}
    </div>
  );
}
